import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ChevronRight } from 'lucide-react';
import { useAchievementsContext } from '@/contexts/AchievementsContext';
import { useTranslation } from '@/hooks/useTranslation';
import { RARITY_CONFIG } from '@/lib/achievements';

export function AchievementsDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { language } = useTranslation();
  const { stats, recentlyUnlocked, markAsSeen, checkAndUnlock } = useAchievementsContext();

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  // Mark as seen when dropdown opens
  useEffect(() => {
    if (open && stats.unseenCount > 0) {
      markAsSeen();
    }
    // Unlock explore_achievements when opening for the first time
    if (open) {
      checkAndUnlock('explore_achievements');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewAll = () => {
    setOpen(false);
    navigate('/settings?tab=achievements');
  };

  return (
    <div className="relative" ref={ref}>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-600/80 transition-colors"
        aria-label={language === 'fr' ? 'Succès' : 'Achievements'}
      >
        <Trophy className="w-[18px] h-[18px] text-gray-600 dark:text-gray-300" />

        {/* Unseen indicator */}
        {stats.unseenCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#16c47f] text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-800">
            {stats.unseenCount > 9 ? '9+' : stats.unseenCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 shadow-lg shadow-black/5 dark:shadow-black/20 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                {language === 'fr' ? 'Ma progression' : 'My Progress'}
              </h3>
              <span className="text-xs font-semibold text-[#16c47f]">
                {stats.unlocked}/{stats.total}
              </span>
            </div>
            {/* Progress bar */}
            <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#16c47f] to-emerald-400 rounded-full transition-all duration-500"
                style={{ width: `${(stats.unlocked / stats.total) * 100}%` }}
              />
            </div>
          </div>

          {/* Recent achievements */}
          <div className="px-2 py-2 max-h-64 overflow-y-auto">
            {recentlyUnlocked.length > 0 ? (
              recentlyUnlocked.map((ach) => {
                const rarity = RARITY_CONFIG[ach.rarity];
                return (
                  <div
                    key={ach.key}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                      ach.status === 'newly_unlocked'
                        ? 'bg-[#16c47f]/5 dark:bg-[#16c47f]/10'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    {/* Icon */}
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${rarity.bgColor}`}
                    >
                      {ach.icon}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {language === 'fr' ? ach.nameFr : ach.nameEn}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                        {ach.unlockedAt
                          ? new Date(ach.unlockedAt).toLocaleDateString(
                              language === 'fr' ? 'fr-FR' : 'en-US',
                              { day: 'numeric', month: 'short' }
                            )
                          : ''}
                      </p>
                    </div>
                    {/* New badge */}
                    {ach.status === 'newly_unlocked' && (
                      <span className="text-[10px] font-bold text-[#16c47f] bg-[#16c47f]/10 px-2 py-0.5 rounded-full flex-shrink-0">
                        {language === 'fr' ? 'Nouveau' : 'New'}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 px-4">
                <div className="text-2xl mb-2">🏆</div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {language === 'fr'
                    ? 'Complétez des actions pour débloquer vos premiers succès !'
                    : 'Complete actions to unlock your first achievements!'}
                </p>
              </div>
            )}
          </div>

          {/* Next achievement hint */}
          {stats.nextAchievement && (
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-700/20">
              <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Prochain succès' : 'Next achievement'}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm">{stats.nextAchievement.icon}</span>
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {language === 'fr' ? stats.nextAchievement.nameFr : stats.nextAchievement.nameEn}
                </span>
                {stats.nextAchievement.progress !== undefined && (
                  <span className="text-[10px] text-gray-400 ml-auto">
                    {Math.round(stats.nextAchievement.progress * 100)}%
                  </span>
                )}
              </div>
            </div>
          )}

          {/* View all button */}
          <div className="px-3 py-2.5 border-t border-gray-100 dark:border-gray-700/50">
            <button
              onClick={handleViewAll}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#16c47f] hover:bg-[#16c47f]/5 rounded-xl transition-colors"
            >
              {language === 'fr' ? 'Voir le détail' : 'View details'}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
